import { isDefined } from "@clipboard-health/util-ts";
import { Stack, type SxProps, type Theme } from "@mui/material";
import { Text } from "@src/appV2/redesign/system/ui-components";

export interface PayRowLabelProps {
  /**
   * The formatted primary pay amount (e.g. "$125.00").
   */
  primaryAmount: string;
  /**
   * Optional formatted hourly rate (e.g. "$25.00").
   * Displayed with "/hr" suffix on the right side.
   */
  hourlyPay?: string;
  /**
   * Optional formatted adjustment/secondary amount (e.g. "(+$15.00)").
   * Displayed next to the primary amount when provided.
   */
  adjustmentAmount?: string;
  /**
   * Optional sx override for the primary amount text.
   */
  primaryAmountSx?: SxProps<Theme>;
  /**
   * Optional sx override for the adjustment amount text.
   */
  adjustmentAmountSx?: SxProps<Theme>;
  /**
   * Custom styles to apply to the root Stack.
   */
  sx?: SxProps<Theme>;
}

export function PayRowLabel(props: PayRowLabelProps) {
  const { primaryAmount, hourlyPay, adjustmentAmount, primaryAmountSx, adjustmentAmountSx, sx } =
    props;

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={sx}>
      <Stack direction="row" alignItems="baseline" spacing={1}>
        <Text semibold variant="h4" sx={primaryAmountSx}>
          {primaryAmount}
        </Text>
        {isDefined(adjustmentAmount) && (
          <Text semibold variant="body1" sx={adjustmentAmountSx}>
            {adjustmentAmount}
          </Text>
        )}
      </Stack>

      {isDefined(hourlyPay) && (
        <Text semibold variant="h5" sx={{ whiteSpace: "nowrap" }}>
          {hourlyPay}/hr
        </Text>
      )}
    </Stack>
  );
}
